/**
 * FikoYT CAM — 3D görselleştirme katmanı (CATIA hissi).
 *  - Seçim: hover (mavi) + seçili (turuncu) kontur/nokta vurgusu, marker'lar
 *  - Makro: clearance/approach yarı saydam düzlemleri + etiket + giriş şekli önizleme
 *  - İnteraktif: makro düzlemini fareyle sürükle → guvenlikZ/yaklasmaZ canlı değişir
 */
import * as THREE from 'three';
import { ICamOperasyon, ICamHareket, ICamMakro, makroBul } from './fiko-cam.types';

export interface GorselDeps {
  sahne: THREE.Scene;
  ciz: () => void;
  kameraAl: () => THREE.Camera | null | undefined;
  canvasAl: () => HTMLCanvasElement | null | undefined;
  stokAl: () => { en: number; boy: number; yuk: number; x: number; y: number; z: number } | undefined;
}

const RENK = { hover: 0x4db8ff, secim: 0xff8c1a, nokta: 0x22dd66, clearance: 0x38bdf8, approach: 0x34d399, giris: 0xffd400, appMotion: 0x34d399, retMotion: 0xef4444 };

export class FikoCamGorsel {
  private grup: THREE.Group;
  private secGrup: THREE.Group;     // seçim/hover vurgu
  private makroGrup: THREE.Group;   // makro düzlem + giriş
  private etiketTex = new Map<string, THREE.Texture>();
  // sürükleme durumu
  private surukle: { op: ICamOperasyon; alan: 'clearance' | 'approach' | 'param'; basY: number; basZ: number; onChange: () => void; makro?: ICamMakro; alan2?: 'yaricap' | 'mesafe' } | null = null;
  private moveH: ((e: PointerEvent) => void) | null = null;
  private upH: ((e: PointerEvent) => void) | null = null;
  private downH: ((e: PointerEvent) => void) | null = null;
  private aktifOp: ICamOperasyon | null = null;
  private onMakroChange: (() => void) | null = null;
  // Paylaşılan kaynaklar (per-marker alloc'u önler) — bosalt()/yokEt() dispose etmez, sadece yokEt sonunda
  private kureGeo = new THREE.SphereGeometry(1, 12, 10);
  private _rc = new THREE.Raycaster();
  private _ndc = new THREE.Vector2();

  constructor(private d: GorselDeps) {
    this.grup = new THREE.Group(); this.grup.name = 'FikoCamGorsel';
    this.secGrup = new THREE.Group(); this.makroGrup = new THREE.Group();
    this.grup.add(this.secGrup); this.grup.add(this.makroGrup);
    (this.grup as any).raycast = () => { };
    this.d.sahne.add(this.grup);
  }

  // ───────── Seçim / hover vurgusu ─────────
  /** Seçili kontur(lar) turuncu + nokta marker'ları. */
  secimVurgu(konturlar?: { x: number; y: number }[][], noktalar?: { x: number; y: number; z: number }[], z = 0): void {
    this.bosalt(this.secGrup);
    for (const k of konturlar || []) if (k.length >= 2) this.secGrup.add(this.konturCizgi(k, z, RENK.secim, 3));
    for (const p of noktalar || []) this.secGrup.add(this.noktaMarker(p.x, p.y, p.z, RENK.nokta, 1.4, true));
    if ((konturlar?.length || noktalar?.length)) this.animBaslat();
    this.d.ciz();
  }
  /** Hover'daki yüz/kontur mavi önizleme (geçici). */
  hoverVurgu(kontur?: { x: number; y: number }[], z = 0): void {
    for (let i = this.secGrup.children.length - 1; i >= 0; i--) { const o: any = this.secGrup.children[i]; if (o.userData?.['hover']) { this.secGrup.remove(o); o.traverse((x: any) => { if (x.geometry && x.geometry !== this.kureGeo) x.geometry.dispose?.(); if (x.material) (Array.isArray(x.material) ? x.material : [x.material]).forEach((m: any) => m.dispose?.()); }); } }
    if (kontur && kontur.length >= 2) { const c = this.konturCizgi(kontur, z, RENK.hover, 2); c.userData['hover'] = true; this.secGrup.add(c); }
    this.d.ciz();
  }
  secimTemizle(): void { this.bosalt(this.secGrup); this.animKontrol(); this.d.ciz(); }

  private konturCizgi(k: { x: number; y: number }[], z: number, renk: number, kalin: number): THREE.Object3D {
    const pts: number[] = []; for (const p of k) pts.push(p.x, p.y, z + 0.05); pts.push(k[0].x, k[0].y, z + 0.05);
    const geo = new THREE.BufferGeometry(); geo.setAttribute('position', new THREE.Float32BufferAttribute(pts, 3));
    const ln = new THREE.Line(geo, new THREE.LineBasicMaterial({ color: renk, depthTest: false, transparent: true, linewidth: kalin }));
    ln.renderOrder = 997; (ln as any).raycast = () => { };
    // köşe noktaları (paylaşılan birim küre + scale)
    const g = new THREE.Group(); g.add(ln);
    for (const p of k) g.add(this.noktaMarker(p.x, p.y, z, renk, 0.8));
    (g as any).raycast = () => { };
    return g;
  }
  private noktaMarker(x: number, y: number, z: number, renk: number, capCarpan = 1, pulse = false): THREE.Object3D {
    const m = new THREE.Mesh(this.kureGeo, new THREE.MeshBasicMaterial({ color: renk, depthTest: false, transparent: true, opacity: 0.95 }));
    const taban = 1.2 * capCarpan; m.scale.setScalar(taban);
    m.position.set(x, y, z + 0.1); m.renderOrder = 998; (m as any).raycast = () => { }; if (pulse) { m.userData['pulse'] = true; m.userData['pulseTaban'] = taban; }
    return m;
  }

  // ───────── Makro görselleştirme ─────────
  /** Seçili operasyonun makro düzlemlerini + giriş şeklini göster (etkileşimli). */
  makroGoster(op: ICamOperasyon | null, toolpath?: ICamHareket[], onChange?: () => void): void {
    this.bosalt(this.makroGrup); this.etiketPurge(); this.aktifOp = op; this.onMakroChange = onChange || null;   // dinamik etiket texture'ları biriktirme (sürükle sırasında Z/param değişir)
    if (!op) { this.dragKapat(); this.d.ciz(); return; }
    const st = this.d.stokAl(); const cx = st ? st.x + st.en / 2 : 0, cy = st ? st.y + st.boy / 2 : 0;
    const w = st ? st.en * 1.15 : 120, h = st ? st.boy * 1.15 : 120;
    const clr = makroBul(op, 'clearance'), app = makroBul(op, 'approach');
    const guvZ = clr?.guvenlikZ ?? app?.guvenlikZ ?? (st ? st.z + st.yuk + 30 : 50);
    const yakZ = app?.yaklasmaZ ?? (st ? st.z + st.yuk + 2 : 2);
    this.makroGrup.add(this.duzlem(cx, cy, w, h, guvZ, RENK.clearance, 'Clearance  Z=' + guvZ.toFixed(0), 'clearance'));
    this.makroGrup.add(this.duzlem(cx, cy, w * 0.92, h * 0.92, yakZ, RENK.approach, 'Approach  Z=' + yakZ.toFixed(0), 'approach'));
    // CATIA: gerçek giriş/çıkış HAREKETLERİ 3D'de (yeşil=yaklaşma, kırmızı=geri çekme) + ok + tip etiketi + sürüklenebilir parametre tutamacı
    if (toolpath?.length) this.makroHareketleri(toolpath, op);
    this.dragKur(); this.animBaslat();
    this.d.ciz();
  }
  makroTemizle(): void { this.bosalt(this.makroGrup); this.aktifOp = null; this.dragKapat(); this.animKontrol(); this.etiketPurge(); this.d.ciz(); }
  /** Etiket sprite texture'larını (Z'ye göre büyüyen 'et' cache) temizle; grid texture (sabit) kalır. */
  private etiketPurge(): void { for (const [k, t] of this.etiketTex) { if (k.startsWith('et')) { t.dispose(); this.etiketTex.delete(k); } } }
  private animKontrol(): void { if (this.secGrup.children.length === 0 && this.makroGrup.children.length === 0) this.animDur(); }

  private duzlem(cx: number, cy: number, w: number, h: number, z: number, renk: number, etiket: string, alan: string): THREE.Object3D {
    const g = new THREE.Group();
    const dop = (this.surukle?.alan === alan) ? 0.34 : 0.18;   // sürüklenen düzlem parlar
    const pg = new THREE.PlaneGeometry(w, h);   // tek geometri: mesh + kenar paylaşır
    const mesh = new THREE.Mesh(pg, new THREE.MeshBasicMaterial({ map: this.gridTex(renk), color: renk, transparent: true, opacity: dop, side: THREE.DoubleSide, depthWrite: false }));
    mesh.position.set(cx, cy, z); mesh.renderOrder = 990; mesh.userData['makroAlan'] = alan;   // raycast hedefi (sürükleme)
    const kenar = new THREE.LineSegments(new THREE.EdgesGeometry(pg), new THREE.LineBasicMaterial({ color: renk, transparent: true, opacity: 0.85, depthTest: false }));
    kenar.position.copy(mesh.position); kenar.renderOrder = 991; (kenar as any).raycast = () => { };
    // köşe L-bracket işaretleri (mühendislik çizim dili)
    const br = Math.min(w, h) * 0.08;
    g.add(mesh); g.add(kenar); g.add(this.koseBracket(cx, cy, w, h, z, renk, br)); g.add(this.etiketSprite(etiket, cx - w / 2, cy + h / 2, z, renk, alan));
    return g;
  }
  /** Düzlem grid dokusu (CATIA referans düzlemi hissi) — şeffaf zemin + grid çizgileri + merkez fade. */
  private gridTex(renk: number): THREE.Texture {
    const key = 'grid' + renk; let tex = this.etiketTex.get(key); if (tex) return tex;
    const S = 256, cv = document.createElement('canvas'); cv.width = cv.height = S; const ctx = cv.getContext('2d')!;
    const hex = '#' + renk.toString(16).padStart(6, '0');
    const grad = ctx.createRadialGradient(S / 2, S / 2, 0, S / 2, S / 2, S / 2);
    grad.addColorStop(0, hex + '30'); grad.addColorStop(1, hex + '08'); ctx.fillStyle = grad; ctx.fillRect(0, 0, S, S);
    ctx.strokeStyle = hex + '55'; ctx.lineWidth = 1; const n = 8;
    for (let i = 0; i <= n; i++) { const p = i / n * S; ctx.beginPath(); ctx.moveTo(p, 0); ctx.lineTo(p, S); ctx.moveTo(0, p); ctx.lineTo(S, p); ctx.stroke(); }
    tex = new THREE.CanvasTexture(cv); tex.colorSpace = THREE.SRGBColorSpace; this.etiketTex.set(key, tex); return tex;
  }
  private koseBracket(cx: number, cy: number, w: number, h: number, z: number, renk: number, br: number): THREE.Object3D {
    const x0 = cx - w / 2, x1 = cx + w / 2, y0 = cy - h / 2, y1 = cy + h / 2;
    const pts: number[] = [];
    const L = (ax: number, ay: number, bx: number, by: number) => { pts.push(ax, ay, z + 0.05, bx, by, z + 0.05); };
    L(x0, y0, x0 + br, y0); L(x0, y0, x0, y0 + br); L(x1, y0, x1 - br, y0); L(x1, y0, x1, y0 + br);
    L(x0, y1, x0 + br, y1); L(x0, y1, x0, y1 - br); L(x1, y1, x1 - br, y1); L(x1, y1, x1, y1 - br);
    const geo = new THREE.BufferGeometry(); geo.setAttribute('position', new THREE.Float32BufferAttribute(pts, 3));
    const ls = new THREE.LineSegments(geo, new THREE.LineBasicMaterial({ color: renk, depthTest: false, transparent: true, opacity: 0.95 })); ls.renderOrder = 992; (ls as any).raycast = () => { };
    return ls;
  }
  /** Toolpath'teki mk='app'/'ret' işaretli ardışık hareket dizilerini (run) çıkar. */
  private makroRunlar(h: ICamHareket[], mk: 'app' | 'ret'): ICamHareket[][] {
    const runlar: ICamHareket[][] = []; let cur: ICamHareket[] | null = null;
    for (let i = 0; i < h.length; i++) {
      if (h[i].mk === mk) { if (!cur) { cur = []; if (i > 0) cur.push(h[i - 1]); runlar.push(cur); } cur.push(h[i]); }
      else cur = null;
    }
    return runlar.filter(r => r.length >= 2);
  }
  /** CATIA giriş/çıkış HAREKETLERİ: yaklaşma (yeşil) + geri çekme (kırmızı) 3D yolları, ok başı, tip etiketi, parametre tutamacı. */
  private makroHareketleri(h: ICamHareket[], op: ICamOperasyon): void {
    const app = makroBul(op, 'approach'), ret = makroBul(op, 'retract');
    const appRun = this.makroRunlar(h, 'app'), retRun = this.makroRunlar(h, 'ret');
    // tüm yaklaşma/geri-çekme run'larını çiz (akan çizgi)
    for (const r of appRun) this.makroGrup.add(this.hareketYolu(r, RENK.appMotion, true));
    for (const r of retRun) this.makroGrup.add(this.hareketYolu(r, RENK.retMotion, false));
    // ilk yaklaşma → tip etiketi + sürüklenebilir yarıçap/mesafe tutamacı
    if (appRun.length && app?.aktif) {
      const r0 = appRun[0]; const giris = r0[0], hedef = r0[r0.length - 1];
      const custom = app.mod === 'custom';
      const tip = app.hareketTip || 'dikine';
      const etk = custom ? 'ÖZEL MAKRO (' + (app.adimlar?.length || 0) + ' adım)' : tip === 'rampa' ? 'RAMPA ' + (app.aci || 3) + '°' : tip === 'helis' ? 'HELİS R' + (app.yaricap || 3) : tip === 'yay' ? 'TEĞET YAY R' + (app.yaricap || 3) : tip === 'on' ? 'ÖN-DELİK' : 'DİKİNE';
      this.makroGrup.add(this.etiketSprite(etk + '  Z=' + hedef.z.toFixed(0), giris.x, giris.y, Math.max(giris.z, hedef.z) + 2, RENK.appMotion, 'mkEtiket'));
      if (!custom && (tip === 'rampa' || tip === 'helis' || tip === 'yay')) {   // hazır modda yarıçap/mesafe tutamacı (custom kendi 2D builder'ı ile ayarlanır)
        const alan2 = tip === 'rampa' ? 'mesafe' : 'yaricap';
        this.makroGrup.add(this.paramTutamac(giris.x, giris.y, giris.z, RENK.appMotion, app, alan2));
      }
    }
    if (retRun.length && ret?.aktif) {
      const r0 = retRun[0]; const cik = r0[r0.length - 1];
      this.makroGrup.add(this.etiketSprite('GERİ ÇEKME', cik.x, cik.y, cik.z + 2, RENK.retMotion, 'mkEtiketR'));
    }
  }
  /** Bir makro hareket dizisini akan, ok başlı 3D yol olarak çiz. */
  private hareketYolu(run: ICamHareket[], renk: number, akan: boolean): THREE.Object3D {
    const g = new THREE.Group();
    const pts: number[] = []; for (const m of run) pts.push(m.x, m.y, m.z);
    const geo = new THREE.BufferGeometry(); geo.setAttribute('position', new THREE.Float32BufferAttribute(pts, 3));
    const ln = new THREE.Line(geo, new THREE.LineDashedMaterial({ color: renk, depthTest: false, transparent: true, dashSize: 2.5, gapSize: 1.6, linewidth: 2, opacity: 0.95 }));
    ln.computeLineDistances(); ln.renderOrder = 996; if (akan) ln.userData['akan'] = true; (ln as any).raycast = () => { };
    g.add(ln);
    // ok başı (son segment yönünde)
    const a = run[run.length - 2], b = run[run.length - 1];
    const dir = new THREE.Vector3(b.x - a.x, b.y - a.y, b.z - a.z); if (dir.lengthSq() > 1e-6) g.add(this.okBasi(b, dir.normalize(), renk));
    (g as any).raycast = () => { };
    return g;
  }
  private okBasi(uc: ICamHareket, dir: THREE.Vector3, renk: number): THREE.Object3D {
    const cone = new THREE.Mesh(new THREE.ConeGeometry(1.2, 4, 10), new THREE.MeshBasicMaterial({ color: renk, depthTest: false, transparent: true, opacity: 0.95 }));
    cone.position.set(uc.x, uc.y, uc.z); cone.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir);
    cone.renderOrder = 997; (cone as any).raycast = () => { }; return cone;
  }
  /** Sürüklenebilir parametre tutamacı (yarıçap/mesafe) — küre + halka. */
  private paramTutamac(x: number, y: number, z: number, renk: number, makro: ICamMakro, alan2: 'yaricap' | 'mesafe'): THREE.Object3D {
    const m = new THREE.Mesh(this.kureGeo, new THREE.MeshBasicMaterial({ color: 0xffffff, depthTest: false, transparent: true, opacity: 0.95 }));
    m.scale.setScalar(2.0); m.position.set(x, y, z + 0.2); m.renderOrder = 998;
    m.userData['makroAlan'] = 'param'; m.userData['makroParam'] = { makro, alan2 };   // sürükleme hedefi
    return m;
  }
  // ───────── Animasyon tick (pulse seçim + akan giriş çizgisi) ─────────
  private rafId = 0; private animVar = false;
  private animBaslat(): void {
    if (this.animVar) return; this.animVar = true;
    const tik = () => { if (!this.animVar) return; this.animGuncelle(); this.rafId = requestAnimationFrame(tik); };
    this.rafId = requestAnimationFrame(tik);
  }
  private animDur(): void { this.animVar = false; if (this.rafId) cancelAnimationFrame(this.rafId); this.rafId = 0; }
  private animGuncelle(): void {
    const t = (typeof performance !== 'undefined' ? performance.now() : 0) * 0.001;
    const s = 1 + Math.sin(t * 4) * 0.22;
    this.secGrup.traverse((o: any) => { if (o.userData?.['pulse']) o.scale.setScalar((o.userData['pulseTaban'] || 1) * s); });
    this.makroGrup.traverse((o: any) => { if (o.userData?.['akan'] && o.material) o.material.dashOffset = -(t * 6) % 1000; });
    this.d.ciz();
  }
  private etiketSprite(metin: string, x: number, y: number, z: number, renk: number, alan = ''): THREE.Sprite {
    const key = 'et' + metin; let tex = this.etiketTex.get(key);
    if (!tex) {
      const hex = '#' + renk.toString(16).padStart(6, '0');
      const [ust, alt] = metin.includes('Z=') ? [metin.split('Z=')[0].trim().toUpperCase(), 'Z = ' + metin.split('Z=')[1].trim()] : [metin.toUpperCase(), ''];
      const W = 280, H = 76, cv = document.createElement('canvas'); cv.width = W; cv.height = H; const ctx = cv.getContext('2d')!;
      const g = ctx.createLinearGradient(0, 0, 0, H); g.addColorStop(0, 'rgba(14,30,48,.92)'); g.addColorStop(1, 'rgba(6,14,26,.94)');
      ctx.fillStyle = g; this.yuvarlakDik(ctx, 3, 3, W - 6, H - 6, 12); ctx.fill();
      ctx.strokeStyle = hex + 'aa'; ctx.lineWidth = 2; this.yuvarlakDik(ctx, 3, 3, W - 6, H - 6, 12); ctx.stroke();
      ctx.fillStyle = hex; this.yuvarlakDik(ctx, 3, 14, 6, H - 28, 3); ctx.fill();   // sol accent bar
      ctx.fillStyle = '#aab8cc'; ctx.font = 'bold 17px system-ui'; ctx.textBaseline = 'middle'; ctx.fillText(ust, 22, alt ? 26 : H / 2);
      if (alt) { ctx.fillStyle = hex; ctx.font = 'bold 26px system-ui'; ctx.fillText(alt, 22, 52); }
      tex = new THREE.CanvasTexture(cv); tex.colorSpace = THREE.SRGBColorSpace; this.etiketTex.set(key, tex);
    }
    const sp = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, depthTest: false, transparent: true }));
    sp.position.set(x, y, z + 0.5); sp.scale.set(30, 8.1, 1); sp.renderOrder = 999; (sp as any).raycast = () => { };
    return sp;
  }
  private yuvarlakDik(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
    ctx.beginPath(); ctx.moveTo(x + r, y); ctx.arcTo(x + w, y, x + w, y + h, r); ctx.arcTo(x + w, y + h, x, y + h, r); ctx.arcTo(x, y + h, x, y, r); ctx.arcTo(x, y, x + w, y, r); ctx.closePath();
  }

  // ───────── İnteraktif sürükleme (makro düzlem Z) ─────────
  private dragKur(): void {
    const cv = this.d.canvasAl(); if (!cv || this.downH) return;
    this.downH = (e) => this.dragBasla(e);
    cv.addEventListener('pointerdown', this.downH, true);
  }
  private dragKapat(): void {
    const cv = this.d.canvasAl();
    if (cv && this.downH) cv.removeEventListener('pointerdown', this.downH, true);
    if (this.moveH) window.removeEventListener('pointermove', this.moveH, true);
    if (this.upH) window.removeEventListener('pointerup', this.upH, true);
    this.downH = this.moveH = this.upH = null; this.surukle = null;
  }
  private dragBasla(e: PointerEvent): void {
    const cv = this.d.canvasAl(), kam = this.d.kameraAl(), op = this.aktifOp; if (!cv || !kam || !op) return;
    const r = cv.getBoundingClientRect();
    this._ndc.set(((e.clientX - r.left) / r.width) * 2 - 1, -((e.clientY - r.top) / r.height) * 2 + 1);
    this._rc.setFromCamera(this._ndc, kam as any);
    const hit = this._rc.intersectObjects(this.makroGrup.children, true).find(h => (h.object as any).userData?.['makroAlan']);
    if (!hit) return;
    const ud = (hit.object as any).userData;
    const alan = ud['makroAlan'] as 'clearance' | 'approach' | 'param';
    e.stopPropagation(); e.preventDefault();
    const mat = (hit.object as any).material; if (mat) mat.opacity = 0.5;   // sürükleme feedback
    if (alan === 'param') {   // yarıçap/mesafe tutamacı: dikey sürükle → parametre
      const p = ud['makroParam'] as { makro: ICamMakro; alan2: 'yaricap' | 'mesafe' };
      const basZ = (p.makro as any)[p.alan2] ?? 3;
      this.surukle = { op, alan, basY: e.clientY, basZ, onChange: this.onMakroChange || (() => { }), makro: p.makro, alan2: p.alan2 };
    } else {
      const m = makroBul(op, alan === 'clearance' ? 'clearance' : 'approach');
      const basZ = alan === 'clearance' ? (m?.guvenlikZ ?? 50) : (m?.yaklasmaZ ?? 2);
      this.surukle = { op, alan, basY: e.clientY, basZ, onChange: this.onMakroChange || (() => { }) };
    }
    this.moveH = (ev) => this.dragHareket(ev); this.upH = () => this.dragBitir();
    window.addEventListener('pointermove', this.moveH, true); window.addEventListener('pointerup', this.upH, true);
  }
  private dragHareket(e: PointerEvent): void {
    const s = this.surukle; if (!s) return;
    const cv = this.d.canvasAl(); const st = this.d.stokAl();
    const olcek = st ? (st.yuk + 60) / (cv?.clientHeight || 600) * 1.5 : 0.2;   // px → mm (yukarı = +Z)
    const yeni = Math.round((s.basZ + (s.basY - e.clientY) * olcek) * 10) / 10;
    if (s.alan === 'param' && s.makro && s.alan2) { (s.makro as any)[s.alan2] = Math.max(0.2, yeni); s.onChange(); return; }
    const m = makroBul(s.op, s.alan === 'clearance' ? 'clearance' : 'approach'); if (!m) return;
    if (s.alan === 'clearance') m.guvenlikZ = yeni; else m.yaklasmaZ = yeni;
    s.onChange();   // yönetici: hesapla + makroGoster yeniden
  }
  private dragBitir(): void {
    if (this.moveH) window.removeEventListener('pointermove', this.moveH, true);
    if (this.upH) window.removeEventListener('pointerup', this.upH, true);
    this.moveH = this.upH = null; this.surukle = null;
  }

  // ───────── Temizlik ─────────
  private bosalt(g: THREE.Group): void {
    for (let i = g.children.length - 1; i >= 0; i--) { const o = g.children[i]; g.remove(o); o.traverse((x: any) => { if (x.geometry && x.geometry !== this.kureGeo) x.geometry.dispose?.(); if (x.material) { (Array.isArray(x.material) ? x.material : [x.material]).forEach((mt: any) => mt.dispose?.()); } }); }
  }
  yokEt(): void { this.animDur(); this.dragKapat(); this.bosalt(this.secGrup); this.bosalt(this.makroGrup); this.d.sahne.remove(this.grup); this.etiketTex.forEach(t => t.dispose()); this.etiketTex.clear(); this.kureGeo.dispose(); }
}
